import { api } from './api';
import type {
  AdminReviewItem,
  AdminReviewItemInput,
  AdminReviewItemUpdate,
  ItemLinkOptions,
  MessageResponse,
  QuestionMappingInput,
  SharedCategoryLink,
  SharedItemLink,
  SharedQuestionMapping,
} from '@exhale/contracts';

export type {
  AdminReviewItemInput,
  AdminReviewItemUpdate,
  ItemLinkOptions,
  QuestionMappingInput,
} from '@exhale/contracts';

export const reviewAdminApi = {
  createItem(itemData: AdminReviewItemInput): Promise<AdminReviewItem> {
    return api.post<AdminReviewItem>('/admin/items', itemData);
  },

  updateItem(itemId: number, updates: AdminReviewItemUpdate): Promise<AdminReviewItem> {
    return api.put<AdminReviewItem>(`/admin/items/${itemId}`, updates);
  },

  linkCategories(sourceCategoryId: number, targetCategoryId: number): Promise<SharedCategoryLink> {
    return api.post<SharedCategoryLink>('/admin/sharing/categories/link', {
      sourceCategoryId,
      targetCategoryId,
    });
  },

  async unlinkCategories(linkId: number): Promise<void> {
    await api.delete<MessageResponse>(`/admin/sharing/categories/unlink/${linkId}`);
  },

  getCategoryLinks(categoryId: number): Promise<SharedCategoryLink[]> {
    return api.get<SharedCategoryLink[]>(`/admin/sharing/categories/${categoryId}/links`);
  },

  linkItems(
    sourceItemId: number,
    targetItemId: number,
    questionMappings: QuestionMappingInput[],
    options?: ItemLinkOptions
  ): Promise<SharedItemLink> {
    return api.post<SharedItemLink>('/admin/sharing/items/link', {
      sourceItemId,
      targetItemId,
      questionMappings,
      includeRoom: options?.includeRoom,
      includePhotos: options?.includePhotos,
      includeFloor: options?.includeFloor,
    });
  },

  async unlinkItems(linkId: number): Promise<void> {
    await api.delete<MessageResponse>(`/admin/sharing/items/unlink/${linkId}`);
  },

  getItemLinks(itemId: number): Promise<SharedItemLink[]> {
    return api.get<SharedItemLink[]>(`/admin/sharing/items/${itemId}/links`);
  },

  addQuestionMapping(
    itemLinkId: number,
    sourceQuestionId: string,
    targetQuestionId: string,
    sharedKey: string
  ): Promise<SharedQuestionMapping> {
    return api.post<SharedQuestionMapping>('/admin/sharing/questions/map', {
      itemLinkId,
      sourceQuestionId,
      targetQuestionId,
      sharedKey,
    });
  },

  async removeQuestionMapping(mappingId: number): Promise<void> {
    await api.delete<MessageResponse>(`/admin/sharing/questions/unmap/${mappingId}`);
  },

  getQuestionMappings(linkId: number): Promise<SharedQuestionMapping[]> {
    return api.get<SharedQuestionMapping[]>(`/admin/sharing/items/${linkId}/questions`);
  },

  getTemplateLinks(templateId: number): Promise<SharedItemLink[]> {
    return api.get<SharedItemLink[]>(`/admin/sharing/templates/${templateId}/links`);
  },
};
